import katex from 'katex';
import { MarkedExtension, Tokens } from 'marked';
import { escapeRegExp } from './string-utils';

interface KatexToken extends Tokens.Generic {
  text: string;
  displayMode: boolean;
}

function bracketKatexExtension(name: string, open: string, close: string, displayMode: boolean): MarkedExtension['extensions'][number] {
  const rule = new RegExp(`^${escapeRegExp(open)}([\\s\\S]+?)${escapeRegExp(close)}`);

  return {
    name,
    level: 'inline',
    start(src: string): number | undefined {
      let offset = 0;
      let remaining = src;

      while (remaining) {
        const index = remaining.indexOf(open);
        if (index === -1) {
          return undefined;
        }
        if (rule.test(remaining.slice(index))) {
          return offset + index;
        }
        offset += index + open.length;
        remaining = remaining.slice(index + open.length);
      }

      return undefined;
    },
    tokenizer(src: string): KatexToken | undefined {
      const match = src.match(rule);
      if (!match) {
        return undefined;
      }
      return {
        type: name,
        raw: match[0],
        text: match[1].trim(),
        displayMode,
      };
    },
    renderer(token: Tokens.Generic) {
      const { text, displayMode } = token as KatexToken;
      return katex.renderToString(text, { displayMode, throwOnError: false });
    },
  };
}

/**
 * The bundled `marked-katex-extension` only recognizes `$…$`/`$$…$$` delimiters. LLM chat
 * responses (GPT, Claude, Gemini, etc.) commonly emit `\( … \)` and `\[ … \]` instead, so this
 * restores KaTeX rendering for those delimiters too.
 */
export function katexBracketDelimitersExtension(): MarkedExtension {
  return {
    extensions: [
      bracketKatexExtension('inlineParenKatex', '\\(', '\\)', false),
      bracketKatexExtension('inlineBracketKatex', '\\[', '\\]', true),
    ],
  };
}
